import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Auth.css';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState(false);

  const { forgotPassword, error: authError, clearError } = useAuth();
  const navigate = useNavigate();

  // Clear errors when email changes
  React.useEffect(() => {
    clearError();
    setLocalError('');
  }, [email, clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setLocalError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setLocalError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setLocalError('');
    clearError();

    try {
      await forgotPassword(email.trim());
      setSuccess(true);
    } catch (error: any) {
      // Error is handled by AuthContext
      if (!authError) {
        setLocalError(error.message || 'Failed to send reset code');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">
              <span className="logo-icon">🎒</span>
              <h1>Simple Packing</h1>
            </div>
            <h2>Check Your Email</h2>
            <p>We've sent a password reset code to <strong>{email}</strong></p>
          </div>

          <div className="auth-form">
            <div className="success-message">
              Please check your email for a password reset code and follow the instructions.
            </div>

            <button
              type="button"
              className="auth-button primary"
              onClick={() => navigate('/reset-password', { state: { email } })}
            >
              Enter Reset Code
            </button>
          </div>

          <div className="auth-footer">
            <p>
              Remember your password?{' '}
              <Link to="/login" className="auth-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <span className="logo-icon">🎒</span>
            <h1>Trip Packing Planner</h1>
          </div>
          <h2>Reset Password</h2>
          <p>Enter your email address and we'll send you a reset code.</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {(authError || localError) && (
            <div className="error-message">
              {authError?.message || localError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter your email"
            />
          </div>

          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Reset Code'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login" className="auth-link">
              Sign in
            </Link>
          </p>
          <p>
            Don't have an account?{' '}
            <Link to="/signup" className="auth-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;